import { Suspense, useCallback, useEffect, useState } from 'react';
import { Outlet, useParams, useLocation } from 'react-router-dom';
import TermsConsentBanner from './TermsConsentBanner';
import { useAuthStore } from '@/lib/stores/auth';
import { api } from '@/lib/api';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { DashboardNav } from '@/components/dashboard/DashboardNav';
import { SpaceProfileCard } from '@/components/dashboard/SpaceProfileCard';
import type { ProfileUser } from '@/pages/UserProfileLayout';

export interface SpaceContext {
  profile: ProfileUser | null;
  isSelf: boolean;
  followVersion: number;
}

/**
 * dashboard 与 users/:id 的共享外壳：顶部资料卡 + tab 条 + Outlet。
 *
 * 把 SpaceProfileCard/DashboardNav 提到这里，跨 /dashboard ↔ /users/:id 切换时
 * 它们不重挂载（同一 React 实例）——DashboardNav 的 tab 滑动指示器因此能原生
 * transition 滑动，而不是因组件重建丢掉动画（之前的「闪一下」根因）。
 *
 * 数据：/dashboard（无 :id）渲染 me；/users/:id 拉 GET /api/users/:id。
 * context（profile/isSelf/followVersion）透传给子路由（UserProfileLayout 及其孙页）。
 */
export function SpaceLayout() {
  const { id: routeId } = useParams<{ id: string }>();
  const location = useLocation();
  // `:id` collides across routes under this pathless layout: `users/:id` is a user id,
  // but `dashboard/sessions/:id`, `dashboard/orders/:id`, admin `:id` etc. are other
  // entities (react-router surfaces the matched `:id` to the layout). Only treat the
  // param as a user id on the /users/* branch — otherwise we'd fetch e.g.
  // /api/users/{sessionId} and 404.
  const id = location.pathname.includes('/users/') ? routeId : undefined;
  const { user: me, refreshUser } = useAuthStore();
  const [profile, setProfile] = useState<ProfileUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [followVersion, setFollowVersion] = useState(0);

  useEffect(() => {
    if (!id) {
      setProfile(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .get<ProfileUser>(`/api/users/${id}`)
      .then((r) => setProfile(r))
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, [id]);

  // dashboard 视图依赖 me 的 followers/following（与 /users/:id 同源计数），保持同步
  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const isSelf = !!me && (!id || me.id === id);

  const handleProfileChange = useCallback(() => {
    if (id) {
      api.get<ProfileUser>(`/api/users/${id}`).then((r) => setProfile(r));
    } else {
      refreshUser();
    }
    setFollowVersion((v) => v + 1);
  }, [id, refreshUser]);

  // 自视图 loading 时用 me 兜底资料卡，避免 /dashboard → /users/me.id 切换时资料卡闪一下
  const cardUser = id ? (profile ?? (isSelf ? me : null)) : me;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {cardUser && (
        <SpaceProfileCard user={cardUser} isSelf={isSelf} onProfileChange={handleProfileChange} />
      )}
      {isSelf && (
        <div className="mt-4">
          <DashboardNav />
        </div>
      )}
      {/* loading 时只 content 区显示 spinner，DashboardNav/资料卡保持渲染，
          tab 指示器不重 mount，切换 tab 的滑动动画才连贯 */}
      {id && loading ? (
        <LoadingSpinner />
      ) : (
        <Suspense fallback={<LoadingSpinner />}>
          <TermsConsentBanner />
          <Outlet context={{ profile, isSelf, followVersion } satisfies SpaceContext} />
        </Suspense>
      )}
    </div>
  );
}
